import React from "react";
import { Search } from "lucide-react";
import {
  SERVICE_BRANCHES,
  SERVICE_BRANCH_LABELS,
} from "../../lib/serviceConstants";
import { DatasetSelectorGroup, DatasetSelectorOption } from "../ui/dataset-selector";
import { ListControls, ListPagination, ListSelectorRow } from "../ui/list-table";
import {
  PassiveBadge,
  formatPresentationDate,
} from "../ui/data-presentation";

export const SERVICE_LIST_BRANCHES = [
  SERVICE_BRANCHES.JAKARTA,
  SERVICE_BRANCHES.SURABAYA,
  SERVICE_BRANCHES.BALI,
];

const BRANCH_TAB_CLASSES = {
  [SERVICE_BRANCHES.JAKARTA]: "coral",
  [SERVICE_BRANCHES.SURABAYA]: "sage",
  [SERVICE_BRANCHES.BALI]: "powder-blue",
};

export function formatServiceListDate(value) {
  return formatPresentationDate(value, { timeZone: "UTC" });
}

export const SERVICE_DVT_PRESENTATIONS = Object.freeze({
  doa: {
    label: "DOA",
    tone: "info",
  },
  in_warranty: {
    label: "IW",
    tone: "success",
  },
  out_of_warranty: {
    label: "OOW",
    tone: "danger",
  },
  void: {
    label: "Void",
    tone: "warning",
  },
});

export function ServiceDvtBadge({ status }) {
  const presentation = SERVICE_DVT_PRESENTATIONS[status];
  if (!presentation) return <span>—</span>;
  return (
    <PassiveBadge
      tone={presentation.tone}
      data-testid={`service-dvt-${status}`}
    >
      {presentation.label}
    </PassiveBadge>
  );
}

export function ServiceBranchSearchRow({
  selectedBranch,
  onBranchChange,
  searchValue,
  onSearchChange,
  beforeSearch = null,
  searchControl = null,
  searchPlaceholder = "Search ticket, customer, serial number, or model",
  searchAriaLabel = "Search Service tickets",
  testIdPrefix = "service",
}) {
  return (
    <ListControls className="gap-0 border-b-0">
      <ListSelectorRow className={beforeSearch || searchControl ? "xl:flex-nowrap xl:gap-6" : "md:flex-wrap md:gap-6"}>
        <DatasetSelectorGroup
          className={`w-full ${beforeSearch || searchControl ? "xl:w-auto xl:flex-nowrap" : "md:w-auto md:flex-nowrap"}`}
          aria-label="Branch selector"
          data-testid={`${testIdPrefix}-branch-tabs`}
        >
          {SERVICE_LIST_BRANCHES.map((branch) => {
            const active = selectedBranch === branch;
            return (
              <DatasetSelectorOption
                key={branch}
                onClick={() => onBranchChange(branch)}
                selected={active}
                accent={BRANCH_TAB_CLASSES[branch]}
                className="w-[172px] max-w-[172px]"
                data-testid={`${testIdPrefix}-branch-${branch}`}
              >
                {SERVICE_BRANCH_LABELS[branch]}
              </DatasetSelectorOption>
            );
          })}
        </DatasetSelectorGroup>
        <div className={`mb-2 flex w-full flex-col gap-2 sm:flex-row sm:items-stretch ${beforeSearch || searchControl ? "xl:ml-auto xl:w-auto" : "md:ml-auto md:w-auto"}`}>
          {beforeSearch}
          {searchControl || <div className="relative w-full min-w-0 rounded-sm shadow-sm sm:flex-1 md:w-[380px] md:flex-none">
            <Search
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
              strokeWidth={2}
            />
            <input
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchAriaLabel}
              className="h-[42px] w-full rounded-sm border border-zinc-200 bg-white py-0 pl-9 pr-3 text-sm text-zinc-900 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/10 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              data-testid={`${testIdPrefix}-search-input`}
            />
          </div>}
        </div>
      </ListSelectorRow>
    </ListControls>
  );
}

export function ServiceListPagination({
  page,
  pageSize = 10,
  total,
  totalPages,
  loading,
  onPrevious,
  onNext,
  onPageSizeChange,
  testIdPrefix = "service",
}) {
  return (
    <ListPagination
      page={page}
      pageSize={pageSize}
      total={total}
      totalPages={totalPages}
      loading={loading}
      onPrevious={onPrevious}
      onNext={onNext}
      onPageSizeChange={onPageSizeChange}
      pageSizeAriaLabel="Service tickets per page"
      summaryTestId={`${testIdPrefix}-result-summary`}
      pageTestId={`${testIdPrefix}-page-summary`}
    />
  );
}
